import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Target, Star, Calendar, BarChart3, Youtube, FileText, Settings } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';
import PlayerCard from '../components/PlayerCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.includes('/api/files/')) {
    const path = url.substring(url.indexOf('/api/files/'));
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

const RankingsPage = () => {
  const [leaderboards, setLeaderboards] = useState({ top_ranking: [], top_scorers: [], top_assists: [] });
  const [allPlayers, setAllPlayers] = useState([]);
  const [cardPlayer, setCardPlayer] = useState(null);

  useEffect(() => {
    fetchLeaderboards();
    fetchAllPlayers();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const response = await axios.get(`${API}/leaderboards`);
      setLeaderboards(response.data);
    } catch (error) {
      console.error('Erro ao carregar líderes:', error);
    }
  };

  const fetchAllPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setAllPlayers(response.data);
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
    }
  };

  const medals = ['🥇', '🥈', '🥉'];
  const medalColors = ['bg-yellow-100 border-yellow-400', 'bg-zinc-100 border-zinc-400', 'bg-amber-100 border-amber-600'];

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">
            ⚽ DOMINGÃO
          </a>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-home">
              HOME
            </a>
            <a href="/votar" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-vote">
              🗳️ VOTAR
            </a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats">
              <BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS
            </a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-matches">
              <Calendar className="inline mr-1" size={16} /> PARTIDAS
            </a>
            <a href="/rankings" className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-rankings">
              <Trophy className="inline mr-1" size={16} /> RANKINGS
            </a>
            <a href="/replays" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-replays">
              <Youtube className="inline mr-1" size={16} /> REPLAYS
            </a>
            <a href="/regras" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rules">
              <FileText className="inline mr-1" size={16} /> REGRAS
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-black tracking-tight uppercase flex items-center gap-2">
            <Trophy size={28} /> DESTAQUES DO MÊS
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Os melhores jogadores do mês — zera no fechamento mensal</p>
        </div>

        {/* Top 3 Featured Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* MVP */}
          {leaderboards.top_ranking[0] && (
            <div className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl p-6 text-white" data-testid="mvp-card">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">🏆</span>
                <span className="text-sm font-bold uppercase tracking-wider opacity-80">LÍDER - Pontos_Rating</span>
              </div>
              {(() => {
                const player = allPlayers.find(p => p.name === leaderboards.top_ranking[0].name);
                const imageUrl = player ? getImageUrl(player.highlight_image_url || player.profile_image_url) : null;
                return (
                  <div className="flex items-center gap-4">
                    {imageUrl ? (
                      <img src={imageUrl} alt={leaderboards.top_ranking[0].name} className="w-16 h-16 rounded-full object-cover border-4 border-white/50" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-white/30 flex items-center justify-center text-2xl font-bold">
                        {leaderboards.top_ranking[0].name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-xl font-black">{leaderboards.top_ranking[0].name}</p>
                      <p className="text-2xl font-black">{leaderboards.top_ranking[0].value.toFixed(2)} pts</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Artilheiro */}
          {leaderboards.top_scorers[0] && leaderboards.top_scorers[0].value > 0 && (
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white" data-testid="artilheiro-card">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">⚽</span>
                <span className="text-sm font-bold uppercase tracking-wider opacity-80">Artilheiro</span>
              </div>
              {(() => {
                const player = allPlayers.find(p => p.name === leaderboards.top_scorers[0].name);
                const imageUrl = player ? getImageUrl(player.highlight_image_url || player.profile_image_url) : null;
                return (
                  <div className="flex items-center gap-4">
                    {imageUrl ? (
                      <img src={imageUrl} alt={leaderboards.top_scorers[0].name} className="w-16 h-16 rounded-full object-cover border-4 border-white/50" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-white/30 flex items-center justify-center text-2xl font-bold">
                        {leaderboards.top_scorers[0].name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-xl font-black">{leaderboards.top_scorers[0].name}</p>
                      <p className="text-2xl font-black">{leaderboards.top_scorers[0].value} gols</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Garçom */}
          {leaderboards.top_assists[0] && leaderboards.top_assists[0].value > 0 && (
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white" data-testid="garcom-card">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">🎯</span>
                <span className="text-sm font-bold uppercase tracking-wider opacity-80">Garçom</span>
              </div>
              {(() => {
                const player = allPlayers.find(p => p.name === leaderboards.top_assists[0].name);
                const imageUrl = player ? getImageUrl(player.highlight_image_url || player.profile_image_url) : null;
                return (
                  <div className="flex items-center gap-4">
                    {imageUrl ? (
                      <img src={imageUrl} alt={leaderboards.top_assists[0].name} className="w-16 h-16 rounded-full object-cover border-4 border-white/50" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-white/30 flex items-center justify-center text-2xl font-bold">
                        {leaderboards.top_assists[0].name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-xl font-black">{leaderboards.top_assists[0].name}</p>
                      <p className="text-2xl font-black">{leaderboards.top_assists[0].value} assist.</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Ranking Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Ranking */}
          <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden" data-testid="ranking-top">
            <div className="bg-yellow-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                <Star size={20} /> TOP 5 PONTOS_RATING
              </h2>
            </div>
            <div className="p-4">
              {leaderboards.top_ranking.length === 0 ? (
                <p className="text-center text-zinc-400 py-4">Nenhum dado</p>
              ) : (
                <div className="space-y-2">
                  {leaderboards.top_ranking.slice(0, 5).map((item, index) => {
                    const player = allPlayers.find(p => p.name === item.name);
                    const imageUrl = player ? getImageUrl(player.profile_image_url) : null;
                    const isTop3 = index < 3;
                    
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setCardPlayer(item.name)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${isTop3 ? medalColors[index] : 'hover:bg-zinc-50'} border ${isTop3 ? 'border-2' : 'border-transparent'} transition-colors`}
                        data-testid={`open-card-rank-${item.name}`}
                      >
                        <span className="w-8 text-center font-black">
                          {isTop3 ? medals[index] : index + 1}
                        </span>
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">
                            {item.name.charAt(0)}
                          </div>
                        )}
                        <span className="flex-1 font-bold text-sm truncate">{item.name}</span>
                        <span className="font-black text-yellow-600">{item.value.toFixed(2)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Top Scorers */}
          <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden" data-testid="ranking-scorers">
            <div className="bg-green-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                ⚽ ARTILHEIROS
              </h2>
            </div>
            <div className="p-4">
              {leaderboards.top_scorers.length === 0 || leaderboards.top_scorers[0]?.value === 0 ? (
                <p className="text-center text-zinc-400 py-4">Nenhum gol registrado</p>
              ) : (
                <div className="space-y-2">
                  {leaderboards.top_scorers.filter(s => s.value > 0).map((item, index) => {
                    const player = allPlayers.find(p => p.name === item.name);
                    const imageUrl = player ? getImageUrl(player.profile_image_url) : null;
                    const isTop3 = index < 3;
                    
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setCardPlayer(item.name)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${isTop3 ? medalColors[index] : 'hover:bg-zinc-50'} border ${isTop3 ? 'border-2' : 'border-transparent'} transition-colors`}
                        data-testid={`open-card-scorer-${item.name}`}
                      >
                        <span className="w-8 text-center font-black">
                          {isTop3 ? medals[index] : index + 1}
                        </span>
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">
                            {item.name.charAt(0)}
                          </div>
                        )}
                        <span className="flex-1 font-bold text-sm truncate">{item.name}</span>
                        <span className="font-black text-green-600">{item.value} gols</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Top Assists */}
          <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden" data-testid="ranking-assists">
            <div className="bg-blue-500 text-white p-4">
              <h2 className="text-lg font-black flex items-center gap-2">
                🎯 GARÇONS
              </h2>
            </div>
            <div className="p-4">
              {leaderboards.top_assists.length === 0 || leaderboards.top_assists[0]?.value === 0 ? (
                <p className="text-center text-zinc-400 py-4">Nenhuma assistência registrada</p>
              ) : (
                <div className="space-y-2">
                  {leaderboards.top_assists.filter(s => s.value > 0).map((item, index) => {
                    const player = allPlayers.find(p => p.name === item.name);
                    const imageUrl = player ? getImageUrl(player.profile_image_url) : null;
                    const isTop3 = index < 3;
                    
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setCardPlayer(item.name)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${isTop3 ? medalColors[index] : 'hover:bg-zinc-50'} border ${isTop3 ? 'border-2' : 'border-transparent'} transition-colors`}
                        data-testid={`open-card-assist-${item.name}`}
                      >
                        <span className="w-8 text-center font-black">
                          {isTop3 ? medals[index] : index + 1}
                        </span>
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold">
                            {item.name.charAt(0)}
                          </div>
                        )}
                        <span className="flex-1 font-bold text-sm truncate">{item.name}</span>
                        <span className="font-black text-blue-600">{item.value} assist.</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Link to full stats */}
        <div className="mt-8 text-center">
          <a 
            href="/estatisticas" 
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-6 py-3 rounded-xl font-bold hover:bg-zinc-800 transition-colors"
            data-testid="link-full-stats"
          >
            <BarChart3 size={20} />
            VER ESTATÍSTICAS COMPLETAS
          </a>
        </div>
      </main>
      {cardPlayer && <PlayerCard playerName={cardPlayer} onClose={() => setCardPlayer(null)} />}
    </div>
  );
};

export default RankingsPage;
